// src/components/kuestion/kb/FeedbackBlock.jsx
//
// The block under every article: "Did this answer it?" → yes / not quite /
// email support (KB feedback & support contract, 18 Sep 2026; client
// response v4 §2).
//
//   ask          the question and the three actions
//   gap          "Not quite" asks the one question that makes the answer
//                usable — what were you looking for?
//   support      subject + a 200-word description; the context (article,
//                account, app) is attached server-side, never typed
//   sent-*       the confirmation for each
//
// A rating is one tap and sent optimistically — a reader is never asked to
// retry a thumbs-up. Text the reader wrote is different: a gap or a ticket
// that could not be sent says so and keeps what was typed.
import React, { useState } from 'react';
import { KUESTION_SUPPORT_EMAIL } from '../../../config/apiConfig';
import { sendRating, sendGap, sendSupport, wordCount, SUPPORT_LIMITS } from './kbApi';
import { KBNUM } from './kbArticles';

const InfoGlyph = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><line x1="12" y1="8" x2="12" y2="8" /><line x1="12" y1="11.5" x2="12" y2="16.5" /></svg>
);
const TickGlyph = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);

const HEAD = {
  ask: 'Did this answer it?',
  gap: 'What was missing?',
  support: 'Tell us what happened',
  'sent-yes': 'Thanks for the feedback',
  'sent-gap': 'Thanks — that is the useful part',
  'sent-support': 'Sent to support',
};
const BODY = {
  ask: 'If something here is wrong or missing, tell us — the article gets fixed, not just your ticket.',
  gap: 'A thumbs-down tells us an article failed. This tells us how.',
  support: 'Two fields. We attach the context ourselves.',
  'sent-yes': 'Good to know this one works — it tells us which articles to leave alone.',
  'sent-gap': 'This goes to the people who write these articles, and the article gets fixed rather than your ticket getting answered.',
  'sent-support': 'A human reads every one of these.',
};

// The reply promise depends on who is asking (client response v4 §2.1): a
// signed-in account has an address, a student has none, and an anonymous
// reader is asked to put one in the message rather than in a field we would
// then ignore.
const replyLine = (kind) => {
  if (kind === 'user') return 'Replies go to the email address on your account.';
  if (kind === 'student') return 'Students have no email address on file, so nobody can reply to you directly. If you need an answer, ask your teacher to raise it.';
  return 'You are not signed in. If you would like a reply, put an email address in your message — we read these by hand, so allow a working day.';
};

const problemCopy = (problem, what) => {
  if (!problem) return null;
  if (problem.kind === 'rate_limited') {
    const mins = problem.retryAfter ? Math.ceil(problem.retryAfter / 60) : null;
    const when = mins && mins < 55 ? `in about ${mins} ${mins === 1 ? 'minute' : 'minutes'}` : 'in about an hour';
    return `You have sent a few of these already — try again ${when}. What you typed is kept here.`;
  }
  if (problem.kind === 'too_long') return `Trim the description to ${SUPPORT_LIMITS.descriptionWords} words.`;
  if (problem.kind === 'required') return 'A description is required.';
  return what === 'support'
    ? 'We could not reach support just now. Your text is kept here — try again in a moment, or email us directly.'
    : 'We could not send that just now. Your text is kept here — try again in a moment.';
};

export default function FeedbackBlock({ article, session }) {
  const [fb, setFb] = useState('ask');
  const [gapText, setGapText] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [reference, setReference] = useState(null);
  const token = session?.token || null;

  const gapWords = wordCount(gapText);
  const supWords = wordCount(body);
  const supReady = Boolean(subject.trim()) && supWords > 0 && supWords <= SUPPORT_LIMITS.descriptionWords;
  const supHint = !subject.trim() ? 'A subject is required'
    : supWords === 0 ? 'A description is required'
      : supWords > SUPPORT_LIMITS.descriptionWords ? `Trim to ${SUPPORT_LIMITS.descriptionWords} words`
        : 'Usually answered within one working day';

  const rate = (helpful) => {
    sendRating({ articleId: article.id, helpful, token }).catch((e) => console.info('kb: rating not recorded', e.kind)); // eslint-disable-line no-console
  };
  const yes = () => { setFb('sent-yes'); rate(true); };
  const no = () => { setFb('gap'); setGapText(''); setProblem(null); rate(false); };
  const cancel = () => { setFb('ask'); setProblem(null); };
  const openSupport = () => {
    // Carry the article across so the subject is not blank, and fold in
    // anything already typed rather than making them retype it.
    setSubject((s) => s || article.title);
    setBody((b) => b || gapText);
    setProblem(null);
    setFb('support');
  };

  const submitGap = async () => {
    if (!gapWords || busy) return;
    setBusy(true); setProblem(null);
    try { await sendGap({ articleId: article.id, text: gapText, token }); setFb('sent-gap'); }
    catch (e) { setProblem({ kind: e.kind, retryAfter: e.retryAfter }); }
    finally { setBusy(false); }
  };

  const submitSupport = async () => {
    if (!supReady || busy) return;
    setBusy(true); setProblem(null);
    try {
      const r = await sendSupport({ subject, description: body, articleId: article.id, articleTitle: article.title, articleNumber: KBNUM[article.id], token });
      setReference(r.reference);
      setFb('sent-support');
    } catch (e) { setProblem({ kind: e.kind, retryAfter: e.retryAfter }); }
    finally { setBusy(false); }
  };

  const mailto = `mailto:${KUESTION_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject || article.title)}&body=${encodeURIComponent(`${body}\n\nArticle: ${article.title} (${article.id})`)}`;
  const sent = fb.indexOf('sent') === 0;

  return (
    <div className="kqkb-fb" aria-live="polite">
      <div className="kqkb-fb__head">
        <div className="kqkb-fb__title">{HEAD[fb]}</div>
        <div className="kqkb-fb__body">{BODY[fb]}</div>
        {fb === 'ask' && (
          <div className="kqkb-fb__actions">
            <button type="button" className="btn btn-secondary" onClick={yes}>Yes, thanks</button>
            <button type="button" className="btn btn-secondary" onClick={no}>Not quite</button>
            <button type="button" className="btn btn-ghost" onClick={openSupport}>Email support</button>
          </div>
        )}
      </div>

      {fb === 'gap' && (
        <div className="kqkb-fb__form">
          <label className="kqkb-fb__label" htmlFor="kb-gap">What were you expecting to find?</label>
          <textarea id="kb-gap" rows="4" value={gapText} onChange={(e) => setGapText(e.target.value)} maxLength={SUPPORT_LIMITS.gapChars} placeholder="e.g. how to give one student extra time on a proctored exam" />
          {problem && <div className="kqkb-fb__problem" role="alert" style={{ marginTop: 10 }}><InfoGlyph /><span>{problemCopy(problem, 'gap')}</span></div>}
          <div className="kqkb-fb__row">
            <button type="button" className="kqkb-fb__send" onClick={submitGap} disabled={!gapWords || busy}>{busy ? 'Sending…' : 'Send feedback'}</button>
            <button type="button" className="btn btn-ghost" onClick={cancel}>Cancel</button>
            <span className={`kqkb-fb__count${gapWords > SUPPORT_LIMITS.descriptionWords ? ' is-over' : ''}`}>{gapWords === 0 ? 'A sentence is enough' : `${gapWords} ${gapWords === 1 ? 'word' : 'words'}`}</span>
          </div>
          <div className="kqkb-fb__fine">Goes to the team who write these articles. No reply unless you ask for one &mdash; for that, use <button type="button" className="kqkb-linkbtn" onClick={openSupport}>email support</button>.</div>
        </div>
      )}

      {fb === 'support' && (
        <div className="kqkb-fb__form">
          <div className="kqkb-fb__formhead"><strong>Email support</strong><span>{KUESTION_SUPPORT_EMAIL}</span></div>

          <label className="kqkb-fb__label" htmlFor="kb-sup-subject">Subject</label>
          <input id="kb-sup-subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={SUPPORT_LIMITS.subjectChars} placeholder="One line — what is wrong" />

          <label className="kqkb-fb__label" htmlFor="kb-sup-body">Description</label>
          <textarea id="kb-sup-body" rows="7" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What you did, what happened, and what you expected instead. Include a login ID or exam name if it helps." />
          <div className="kqkb-fb__row" style={{ marginTop: 8 }}>
            <span className={`kqkb-fb__count${supWords > SUPPORT_LIMITS.descriptionWords ? ' is-over' : ''}`} style={{ marginLeft: 0 }}>{`${supWords} / ${SUPPORT_LIMITS.descriptionWords} words${supWords > SUPPORT_LIMITS.descriptionWords ? ' — over the limit' : ''}`}</span>
          </div>

          <div className="kqkb-fb__info">
            <InfoGlyph />
            <span>We attach the article you were reading, your account and the app you are using, so you do not have to describe them. <strong>Never include a password</strong> &mdash; we can reset one without ever seeing it. {replyLine(session?.kind)}</span>
          </div>

          {problem && (
            <div className="kqkb-fb__problem" role="alert">
              <InfoGlyph />
              <span>{problemCopy(problem, 'support')}{!['rate_limited','too_long','required'].includes(problem.kind) && <> Or <a href={mailto}>open this in your mail app</a>.</>}</span>
            </div>
          )}

          <div className="kqkb-fb__row" style={{ marginTop: 0 }}>
            <button type="button" className="kqkb-fb__send kqkb-fb__send--lg" onClick={submitSupport} disabled={!supReady || busy}>{busy ? 'Sending…' : 'Send to support'}</button>
            <button type="button" className="btn btn-ghost" onClick={cancel}>Cancel</button>
            <span className="kqkb-fb__hint">{supHint}</span>
          </div>
        </div>
      )}

      {sent && (
        <div className="kqkb-fb__sent">
          <span className="kqkb-fb__tick"><TickGlyph /></span>
          <span className="kqkb-fb__senttext">
            {fb === 'sent-support' ? (
              <>
                <span className="kqkb-fb__senthead">Ticket raised{reference ? <> &middot; <span className="kqkb-fb__ref">{reference}</span></> : null}</span>
                <span className="kqkb-fb__sentbody">
                  We have your subject, description, and the article and account you were on.{' '}
                  {session?.kind === 'student'
                    ? <>Students have no email address on file, so there is nowhere to reply to &mdash; your teacher can follow this up{reference ? <> quoting the reference above</> : null}.</>
                    : session?.kind === 'user'
                      ? <>Replies go to the email address on your account{reference ? <>; quote the reference if you write again</> : null}.</>
                      : <>If you put an email address in the message, the reply goes there{reference ? <>; quote the reference if you write again</> : null}.</>}
                </span>
              </>
            ) : (
              // A rating is not the end of the road: anyone who mis-clicked can
              // change their answer, and support is still one press away.
              <span className="kqkb-fb__actions" style={{ marginTop: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={openSupport}>Email support</button>
                <button type="button" className="btn btn-ghost" onClick={cancel}>Change my answer</button>
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
